/**
 * Name validation utilities
 * Handles validation for profile names based on account type
 */

// Forbidden special characters
const FORBIDDEN_CHARS_REGEX = /[@#$%*=]/;

// Emoji regex
const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]/gu;

// Multiple consecutive spaces regex
const MULTIPLE_SPACES_REGEX = /\s{2,}/;

/**
 * Validates and sanitizes a profile name (for Breeder/Company)
 * Allowed: letters (including č, ć, ž, š, đ), spaces, numbers, hyphen (-), ampersand (&)
 * Forbidden: special characters (@, #, $, %, *, =), emojis, multiple consecutive spaces, leading/trailing spaces
 * 
 * Note: Leading/trailing spaces are only warned about, not removed during typing (to allow natural typing flow)
 * 
 * @param {string} value - The input value
 * @param {boolean} isFinalValidation - If true, also trims leading/trailing spaces (for blur/submit)
 * @returns {object} - { isValid: boolean, sanitizedValue: string, error: string|null }
 */
export const validateProfileName = (value, isFinalValidation = false) => {
  if (!value) {
    return { isValid: true, sanitizedValue: '', error: null };
  }

  let sanitized = value;
  let error = null;

  // Remove emojis
  const hadEmojis = EMOJI_REGEX.test(sanitized);
  sanitized = sanitized.replace(EMOJI_REGEX, '');

  // Remove forbidden special characters
  const hadForbiddenChars = FORBIDDEN_CHARS_REGEX.test(sanitized);
  sanitized = sanitized.replace(FORBIDDEN_CHARS_REGEX, '');

  // Check for multiple consecutive spaces (warn but replace)
  const hadMultipleSpaces = MULTIPLE_SPACES_REGEX.test(sanitized);
  sanitized = sanitized.replace(MULTIPLE_SPACES_REGEX, ' ');

  // Remove any other invalid characters (keep only letters, spaces, numbers, hyphen, ampersand, dot, quotes)
  sanitized = sanitized.replace(/[^\p{L}\s\d\-&.'"]/gu, '');

  // Only trim on final validation (blur/submit), not during typing
  if (isFinalValidation) {
    sanitized = sanitized.trim();
  }

  // Determine error message - show what IS allowed
  if (hadEmojis || hadForbiddenChars || hadMultipleSpaces) {
    error = 'Allowed: letters, numbers, spaces, hyphen (-), &, dot (.), quotes (\' ")';
  }

  return {
    isValid: !error,
    sanitizedValue: sanitized,
    error
  };
};

/**
 * Validates and sanitizes first/last name (for Individual)
 * Allowed: letters (including č, ć, ž, š, đ), single space (for compound names)
 * Forbidden: numbers, symbols, multiple consecutive spaces
 * 
 * @param {string} value - The input value
 * @param {boolean} isFinalValidation - If true, also trims leading/trailing spaces (for blur/submit)
 * @returns {object} - { isValid: boolean, sanitizedValue: string, error: string|null }
 */
export const validatePersonName = (value, isFinalValidation = false) => {
  if (!value) {
    return { isValid: true, sanitizedValue: '', error: null };
  }

  let sanitized = value;
  let error = null;

  // Remove emojis
  const hadEmojis = EMOJI_REGEX.test(sanitized);
  sanitized = sanitized.replace(EMOJI_REGEX, '');

  // Check for numbers
  const hadNumbers = /\d/.test(sanitized);
  sanitized = sanitized.replace(/\d/g, '');

  // Check for symbols (before removing them)
  const hadSymbols = /[^\p{L}\s]/u.test(sanitized);
  
  // Remove all symbols (keep only letters and spaces)
  sanitized = sanitized.replace(/[^\p{L}\s]/gu, '');

  // Check for multiple consecutive spaces (warn but replace)
  const hadMultipleSpaces = MULTIPLE_SPACES_REGEX.test(sanitized);
  sanitized = sanitized.replace(MULTIPLE_SPACES_REGEX, ' ');

  // Only trim on final validation (blur/submit), not during typing
  if (isFinalValidation) {
    sanitized = sanitized.trim();
  }

  // Determine error message - show what IS allowed
  if (hadEmojis || hadNumbers || hadSymbols || hadMultipleSpaces) {
    error = 'Allowed: letters and spaces only';
  }

  return {
    isValid: !error,
    sanitizedValue: sanitized,
    error
  };
};

/**
 * Gets the appropriate validation function based on account type and field name
 * 
 * @param {string} accountType - 'individual', 'breeder', or 'company'
 * @param {string} fieldName - 'first_name', 'last_name', or 'profile_name'
 * @returns {function} - The validation function to use
 */
export const getNameValidator = (accountType, fieldName) => {
  if (fieldName === 'profile_name') {
    return validateProfileName;
  }
  
  // For first_name and last_name, always use person name validation
  return validatePersonName;
};

/**
 * Extracts the first letter (only letters, no special characters or numbers) from a string
 * 
 * @param {string} str - The input string
 * @returns {string} - The first letter found, or empty string if none
 */
const getFirstLetter = (str) => {
  if (!str) return '';
  const match = str.match(/\p{L}/u);
  return match ? match[0] : '';
};

/**
 * Extracts the first two letters (only letters, no special characters or numbers) from a string
 * 
 * @param {string} str - The input string
 * @returns {string} - The first two letters found, or fewer if not available
 */
const getFirstTwoLetters = (str) => {
  if (!str) return '';
  const matches = str.match(/\p{L}/gu);
  if (!matches) return '';
  return matches.slice(0, 2).join('');
};

/**
 * Gets the initials for a user's profile avatar
 * Only letters are used (no special characters, numbers, or symbols)
 * 
 * - For Individual profile: uses First Name + Last Name (e.g., Marko Marković → MM)
 * - For Breeder/Company profile: uses Profile Name (first letter of first two words, e.g., Sphynx Cat → SC)
 * 
 * @param {object} user - The user object containing account_type, first_name, last_name, profile_name
 * @returns {string} - The initials (1-2 characters)
 */
export const getInitials = (user) => {
  if (!user) return '';
  
  const accountType = user.account_type?.toLowerCase();
  
  // For Individual profile: use first_name + last_name
  if (accountType === 'individual') {
    const firstInitial = getFirstLetter(user.first_name);
    const lastInitial = getFirstLetter(user.last_name);
    return (firstInitial + lastInitial).toUpperCase();
  }
  
  // For Breeder/Company profile: use profile_name (first letter of first two words)
  if (user.profile_name) {
    const words = user.profile_name.trim().split(/\s+/);
    if (words.length >= 2) {
      // Take first letter of first two words (only letters)
      const first = getFirstLetter(words[0]);
      const second = getFirstLetter(words[1]);
      if (first && second) {
        return (first + second).toUpperCase();
      }
      // If one word has no letters, try to get two letters from the other
      if (first) return getFirstTwoLetters(words[0]).toUpperCase();
      if (second) return getFirstTwoLetters(words[1]).toUpperCase();
    } else if (words.length === 1 && words[0].length > 0) {
      // Single word: take first two letters (only letters)
      return getFirstTwoLetters(words[0]).toUpperCase();
    }
  }
  
  // Fallback to first_name + last_name if profile_name is not available
  const firstInitial = getFirstLetter(user.first_name);
  const lastInitial = getFirstLetter(user.last_name);
  return (firstInitial + lastInitial).toUpperCase();
};
